// Components/SetupProgressCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./css/setupProgressCard.css";
import {
  FiCheckCircle,
  FiCircle,
  FiArrowRight,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { createPortal } from "react-dom";

import { useGetAccountQuery } from "../../store/accountApi";
import { useGetRestaurantSetupQuery, usePrefetch } from "../../store/setupApi";

import useNotification from "../../Components/Notification";
import Profile from "../../Pages/Profiel";
import translations from "./translations.json";

import {
  PROFILE_REQUIRED,
  PROFILE_VALIDATORS,
  // keep if you still show per-meal labels elsewhere
  validateOpeningHours,
  // NEW: passes if there's one valid shift anywhere in the week
  validateOpeningHoursAnyShiftWeek,
} from "./utils/setupValidators";

/* ---------------- Modal Portal ---------------- */
function ModalPortal({ children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  return createPortal(children, document.body);
}

/* ---------------- Component ---------------- */
export default function SetupProgressCard({
  routes = {
    profile: "/",
    openingHours: "/openinghours",
    exceptions: "/uitzonderingen",
  },
  className = "",
  autoHideWhenComplete = true,
  onComplete,
}) {
  const navigate = useNavigate();
  const { triggerNotification, NotificationComponent } = useNotification();

  // i18n
  const preferredLanguage = localStorage.getItem("preferredLanguage") || "nl";
  const T = translations[preferredLanguage] || translations["nl"];

  // helper: get http status from RTK Query error shapes
  const getStatus = (err) =>
    Number(
      err?.status ??
        err?.originalStatus ??
        err?.data?.status ??
        err?.data?.statusCode
    ) || null;

  // helper: localize hints (SAFE FALLBACKS + deep replacements)
  const localizeHint = (hint) => {
    if (!hint) return "";
    const low = String(hint).toLowerCase();

    if (low === "done") return T?.status?.done ?? "Done";
    if (low === "not set") return T?.status?.notSet ?? "Not set";

    if (low.startsWith("issues:")) {
      let body = hint.slice(7).trim();

      // Opening-hours specific
      body = body.replace(
        /invalid time/gi,
        T?.opening?.errors?.invalidTime ?? "invalid time"
      );
      body = body.replace(
        /start\s*≥\s*end/gi,
        T?.opening?.errors?.startGteEnd ?? "start ≥ end"
      );
      body = body.replace(
        /add at least one shift/gi,
        T?.opening?.errors?.addOneShift ?? "add at least one shift"
      );
      body = body.replace(/enable at least (\d+)\s+day(s)?/i, (_, n) =>
        (
          T?.opening?.errors?.enableAtLeastDays ||
          "Enable at least {count} day(s)"
        ).replace("{count}", n)
      );

      // Exceptions specific
      body = body.replace(
        /no exceptions/gi,
        T?.exceptions?.errors?.noExceptions ?? "no exceptions"
      );
      body = body.replace(
        /invalid item/gi,
        T?.exceptions?.errors?.invalidItem ?? "invalid item"
      );
      body = body.replace(
        /startHour\/endHour/gi,
        T?.exceptions?.errors?.startEndHour ?? "startHour/endHour"
      );

      // Replace field keys inside exceptions lists
      const exFields = T?.exceptions?.fields || {};
      body = body
        .replace(/\btitle\b/g, exFields.title || "title")
        .replace(/\btype\b/g, exFields.type || "type")
        .replace(/\btimeframe\b/g, exFields.timeframe || "timeframe")
        .replace(/\brange\b/g, T?.exceptions?.errors?.range || "range")
        .replace(/\bstartDate\b/g, exFields.startDate || "start date")
        .replace(/\bendDate\b/g, exFields.endDate || "end date")
        .replace(/\bdaysOfWeek\b/g, exFields.daysOfWeek || "days of week")
        .replace(/\bmaxSeats\b/g, exFields.maxSeats || "max seats");

      return `${T?.words?.issues ?? "Issues"}: ${body}`;
    }

    // kept for safety even though settings are removed
    if (low.startsWith("missing/invalid:")) {
      const raw = hint.slice(16).trim();
      const settingsFields = T?.settings?.fields || {};
      const localized = raw
        .split(",")
        .map((p) => {
          const k = p.trim();
          return settingsFields[k] || k;
        })
        .join(", ");
      return `${T?.words?.missingInvalid ?? "Missing/invalid"}: ${localized}`;
    }

    return hint;
  };

  const PROFILE_LABELS = T.profile.fields;

  // Normalize paths
  const normalizeTo = (to, fallback = "/") => {
    const val =
      typeof to === "string" && to.trim().length ? to.trim() : fallback;
    if (/^(https?:|mailto:|tel:)/i.test(val)) return val;
    return val.startsWith("/") ? val : `/${val}`;
  };
  const PROFILE_TO = normalizeTo(routes.profile, "/profile");
  const OPENING_TO = normalizeTo(routes.openingHours, "/openinghours");
  const EXCEPTIONS_TO = normalizeTo(routes.exceptions, "/settings/exceptions");

  // Collapsed state (persisted)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("spcCollapsed") === "1"
  );
  useEffect(() => {
    localStorage.setItem("spcCollapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  const collapseCard = () => setCollapsed(true);
  const handleToggle = () => setCollapsed((v) => !v);

  // Prefetcher: single endpoint
  const prefetchSetup = usePrefetch("getRestaurantSetup");
  const warmUp = (force = false) => prefetchSetup(undefined, { force });
  const onHeaderEnter = () => warmUp(false);

  // Profile modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const openProfileModal = () => setIsProfileModalOpen(true);
  const closeProfileModal = () => setIsProfileModalOpen(false);
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeProfileModal();
    };
    if (isProfileModalOpen) window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isProfileModalOpen]);

  // Account query
  const {
    data: account,
    isLoading: accountLoading,
    isFetching: accountFetching,
    isError: accountIsError,
  } = useGetAccountQuery(undefined, { refetchOnMountOrArgChange: false });

  // Skip network while collapsed
  const skip = collapsed;

  // Live setup query
  const {
    data: setupLive,
    isFetching: setupFetching,
    isUninitialized: setupUninit,
    isError: setupIsError,
    error: setupError,
  } = useGetRestaurantSetupQuery(undefined, { skip });

  // Detect 404 and treat as "empty"
  const setupIs404 = setupIsError && getStatus(setupError) === 404;

  // Compute a safe "resolvedSetup"
  const DEFAULT_SETUP = useMemo(
    () => ({
      openingHours: { lunch: null, dinner: null, breakfast: null },
      exceptions: [],
    }),
    []
  );
  const resolvedSetup =
    setupLive || (setupIs404 ? DEFAULT_SETUP : DEFAULT_SETUP);

  // Slices from payload
  const ohLunch = resolvedSetup?.openingHours?.lunch ?? null;
  const ohDinner = resolvedSetup?.openingHours?.dinner ?? null;
  const ohBreakfast = resolvedSetup?.openingHours?.breakfast ?? null;
  const exceptions = resolvedSetup?.exceptions ?? [];

  /* ---------- profile completeness + hint (localized) ---------- */
  const { profileDone, profileMissing } = useMemo(() => {
    if (!account)
      return { profileDone: false, profileMissing: PROFILE_REQUIRED };
    const missing = PROFILE_REQUIRED.filter((k) => {
      const validate = PROFILE_VALIDATORS[k];
      return typeof validate === "function"
        ? !validate(account)
        : !account?.[k];
    });
    return { profileDone: missing.length === 0, profileMissing: missing };
  }, [account]);

  const profileHint = profileDone
    ? T.status.done
    : `${T.profile.missingPrefix}${profileMissing
        .slice(0, 3)
        .map((k) => PROFILE_LABELS[k] || k)
        .join(", ")}${profileMissing.length > 3 ? "…" : ""}`;

  /* ---------- opening hours completeness (ONE SHIFT ANYWHERE) ---------- */
  const openingHoursLoading = skip ? false : setupFetching || setupUninit;

  const { openingHoursDone, openingHoursHint } = useMemo(() => {
    // Pass if any meal has one valid shift anywhere in the week
    const agg = validateOpeningHoursAnyShiftWeek({
      breakfast: ohBreakfast,
      lunch: ohLunch,
      dinner: ohDinner,
    });

    const hint = agg.done
      ? T.status.done
      : localizeHint(agg.hint || T.status.notSet);

    return { openingHoursDone: agg.done, openingHoursHint: hint };
  }, [ohBreakfast, ohLunch, ohDinner, T]);

  /* ---------- exceptions completeness (≥1 = done) ---------- */
  const { exceptionsDone, exceptionsHint } = useMemo(() => {
    const hasAny = Array.isArray(exceptions) && exceptions.length > 0;
    return {
      exceptionsDone: hasAny,
      exceptionsHint: hasAny ? T.status.done : T.status.notSet,
    };
  }, [exceptions, T]);

  // Items to render (settings removed)
  const items = useMemo(
    () => [
      {
        key: "profile",
        label: T.labels.profile,
        done: profileDone,
        to: PROFILE_TO,
        hint: profileHint,
      },
      {
        key: "opening",
        label: T.labels.opening,
        done: openingHoursDone,
        to: OPENING_TO,
        loading: openingHoursLoading,
        hint: !skip && !openingHoursLoading ? openingHoursHint : undefined,
      },
      {
        key: "exceptions",
        label: T.labels.exceptions,
        done: exceptionsDone,
        to: EXCEPTIONS_TO,
        loading: skip ? false : setupFetching || setupUninit,
        hint:
          !skip && !(setupFetching || setupUninit) ? exceptionsHint : undefined,
      },
    ],
    [
      T,
      profileDone,
      profileHint,
      openingHoursDone,
      openingHoursLoading,
      openingHoursHint,
      exceptionsDone,
      exceptionsHint,
      skip,
      setupFetching,
      setupUninit,
      PROFILE_TO,
      OPENING_TO,
      EXCEPTIONS_TO,
    ]
  );

  const total = items.length;
  const doneCount = items.filter((i) => i.done).length;
  const progress = Math.round((doneCount / total) * 100);
  const isComplete = doneCount === total;

  // Optional: notify when complete
  const [didNotifyComplete, setDidNotifyComplete] = useState(false);
  useEffect(() => {
    if (isComplete && !didNotifyComplete) {
      setDidNotifyComplete(true);
      try {
        onComplete?.();
      } catch {}
    }
    if (!isComplete && didNotifyComplete) setDidNotifyComplete(false);
  }, [isComplete, didNotifyComplete, onComplete]);

  // ---------- Only decide visibility AFTER all hooks ----------
  // Ensure we wait for ALL data to be 100% loaded before showing anything
  const setupSettled = skip || setupIs404 || Boolean(setupLive);
  const accountReady = Boolean(account) && !accountLoading && !accountFetching;
  const allDataLoaded = setupSettled && accountReady && !accountIsError;

  // Show nothing until all data is fully loaded
  if (!allDataLoaded) {
    return null;
  }

  // Hide when truly complete
  if (autoHideWhenComplete && isComplete) {
    return null;
  }

  // Interactions
  const handleActionClick = (item, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    collapseCard();

    if (item.to === PROFILE_TO) {
      openProfileModal();
      return;
    }
    navigate(item.to);
  };

  const handleLinkClick = (item) => (e) => {
    if (item.to === PROFILE_TO) {
      e.preventDefault();
      e.stopPropagation();
      collapseCard();
      openProfileModal();
    } else {
      collapseCard();
    }
  };

  return (
    <div
      className={`setup-progresscard ${className} ${
        collapsed ? "setup-progresscard--collapsed" : ""
      }`}
      onMouseEnter={onHeaderEnter}
    >
      <NotificationComponent />

      <div className="setup-progresscard__header">
        <button
          type="button"
          className="setup-progresscard__toggle"
          onClick={handleToggle}
          aria-expanded={!collapsed}
          aria-controls="spc-body"
          title={collapsed ? T.ui.expand : T.ui.collapse}
        >
          {collapsed ? <FiChevronDown size={18} /> : <FiChevronUp size={18} />}
        </button>

        <div
          className="setup-progresscard__headings"
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleToggle();
          }}
        >
          <h3 className="setup-progresscard__title">{T.title}</h3>
          <p className="setup-progresscard__subtitle">
            {doneCount}/{total} {T.completed}
          </p>
        </div>

        <div className="setup-progresscard__progress">
          <div className="setup-progresscard__progress-bar">
            <div
              className="setup-progresscard__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="setup-progresscard__progress-label">
            {progress}%
          </span>
        </div>
      </div>

      <div
        id="spc-body"
        className={`setup-progresscard__body ${
          collapsed ? "setup-progresscard__body--collapsed" : ""
        }`}
      >
        <ul className="setup-progresscard__list">
          {items.map((item) => (
            <li
              key={item.key}
              className={`setup-progresscard__item ${
                item.done ? "setup-progresscard__item--done" : ""
              }`}
            >
              <div className="setup-progresscard__item-left">
                {item.done ? (
                  <span className="setup-progresscard__icon setup-progresscard__icon--done">
                    <FiCheckCircle size={20} />
                  </span>
                ) : (
                  <span className="setup-progresscard__icon setup-progresscard__icon--pending">
                    <FiCircle size={20} />
                  </span>
                )}

                <span className="setup-progresscard__item-label">
                  {item.label}
                </span>

                {item.hint ? (
                  <span
                    className={`setup-progresscard__item-hint ${
                      item.done
                        ? "setup-progresscard__item-hint--ok"
                        : "setup-progresscard__item-hint--warn"
                    }`}
                  >
                    {item.hint}
                  </span>
                ) : item.loading ? (
                  <span className="setup-progresscard__item-hint">
                    {T.status.checking}
                  </span>
                ) : item.done ? (
                  <span className="setup-progresscard__item-hint setup-progresscard__item-hint--ok">
                    {T.status.done}
                  </span>
                ) : (
                  <span className="setup-progresscard__item-hint setup-progresscard__item-hint--warn">
                    {T.status.notSet}
                  </span>
                )}
              </div>

              {item.done ? (
                <button
                  type="button"
                  className="setup-progresscard__btn setup-progresscard__btn--ghost"
                  onClick={(e) => handleActionClick(item, e)}
                  title={T.status.view}
                >
                  {T.status.view} <FiArrowRight size={16} />
                </button>
              ) : (
                <Link
                  className="setup-progresscard__btn"
                  to={item.to}
                  onClick={handleLinkClick(item)}
                >
                  {T.status.goTo} <FiArrowRight size={16} />
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Profile modal */}
      {isProfileModalOpen && (
        <ModalPortal>
          <div
            className="setup-progresscard__profile-modal-overlay"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (
                e.target.classList.contains(
                  "setup-progresscard__profile-modal-overlay"
                )
              )
                closeProfileModal();
            }}
          >
            <div className="setup-progresscard__profile-modal-content">
              <button
                className="setup-progresscard__profile-modal-close"
                onClick={closeProfileModal}
                aria-label={T.ui.close}
                title={T.ui.close}
              >
                &times;
              </button>

              <Profile
                triggerNotification={triggerNotification}
                closeProfile={closeProfileModal}
              />
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}
